import type {
  Ordinate,
  SheetAttributeMapping,
} from 'services/sheet-attribute-manager/models/sheet-attribute-manager.interface';

const visitOrdinates = (mapping: SheetAttributeMapping, visit: (ordinate: Ordinate, key: string) => void) => {
  mapping.sheetIndicatorMapping.forEach(({ documentReferenceId, sheet, gridIndicators }) => {
    (['columns', 'rows'] as const).forEach(axis => {
      Object.entries(gridIndicators[axis]).forEach(([coordinate, ordinates]) => {
        ordinates.forEach((ordinate, index) => {
          // Coordinates belong to a document and sheet; blank suggestions also need their position.
          const key = JSON.stringify([
            documentReferenceId,
            sheet,
            axis,
            coordinate,
            ordinate.value,
            ordinate.direction,
            ordinate.value ? null : index,
          ]);
          visit(ordinate, key);
        });
      });
    });
  });
};

const isPending = (ordinate: Ordinate) => ordinate.isAiGenerated && !ordinate.isAccepted && !ordinate.isDeclined;

export const capturePendingAIMappings = (mapping: SheetAttributeMapping) => {
  const pendingOrdinateKeys = new Set<string>();
  visitOrdinates(mapping, (ordinate, key) => {
    if (isPending(ordinate)) {
      pendingOrdinateKeys.add(key);
    }
  });
  return { mappingId: mapping.id, generationJobId: mapping.generationJobId, pendingOrdinateKeys };
};

export const acceptCapturedAIMappings = (
  mapping: SheetAttributeMapping,
  snapshot: ReturnType<typeof capturePendingAIMappings>
): SheetAttributeMapping => {
  // A newer job may replace suggestions at identical coordinates, so matching values alone is not enough.
  if (mapping.id !== snapshot.mappingId || mapping.generationJobId !== snapshot.generationJobId) {
    return mapping;
  }

  const acceptedMapping: SheetAttributeMapping = JSON.parse(JSON.stringify(mapping));
  let hasPending = false;
  visitOrdinates(acceptedMapping, (ordinate, key) => {
    if (isPending(ordinate) && snapshot.pendingOrdinateKeys.has(key)) {
      // eslint-disable-next-line no-param-reassign
      ordinate.isAccepted = true;
    }
    hasPending = hasPending || Boolean(isPending(ordinate));
  });
  acceptedMapping.isAccepted = !hasPending;
  return acceptedMapping;
};
